import React from 'react';

import { useContextRequired } from '@nfctron/nexus-react';

import { useSuspenseQuery } from '@tanstack/react-query';

import { type ApiSecurityData, createPassApi, createRootApi } from '@root/features/api-client/api-client';
import { type PassApi } from '@root/features/api-client/generated/pass.api';
import { type RootApi } from '@root/features/api-client/generated/root.api';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { getIntegrator } from '@root/lib/utils/integrators';

/* -------------------------------------------------------------------------------------------------
 * ApiClient / Context
 * -----------------------------------------------------------------------------------------------*/

export interface ApiClientContextProps {
	passApi: PassApi<ApiSecurityData>;
	rootApi: RootApi<ApiSecurityData>;
}

export const ApiClientContext = React.createContext<ApiClientContextProps | undefined>(undefined);

/* -------------------------------------------------------------------------------------------------
 * ApiClient / Provider
 * -----------------------------------------------------------------------------------------------*/

interface ApiClientProviderProps {
	children: React.ReactNode;
}

export function ApiClientProvider(props: ApiClientProviderProps) {
	const { children } = props;
	const { lang: i18nLang } = useTranslate(APP_I18N_NAMESPACE.COMMON);

	/** get api clients */
	const suspensedApis = useSuspenseQuery({
		queryKey: [
			'integrator',
			i18nLang,
			typeof window !== 'undefined'
				? {
						hostname: window.location.hostname,
						pathname: window.location.pathname,
						origin: window.location.origin,
					}
				: null,
		] as const,
		async queryFn({ queryKey }) {
			const [, locale, location] = queryKey;
			const integrator = getIntegrator(location);
			const passApi = createPassApi({ locale, reqSource: integrator.reqSource });
			const rootApi = createRootApi({ locale, reqSource: integrator.reqSource });
			return { passApi, rootApi };
		},
	});

	const apiClientCtx = React.useMemo(() => ({ passApi: suspensedApis.data.passApi, rootApi: suspensedApis.data.rootApi }), [suspensedApis.data]);

	return <ApiClientContext value={apiClientCtx}>{children}</ApiClientContext>;
}

export function useApiClient() {
	const apiClientCtx = useContextRequired(ApiClientContext, 'useApiClient must be used within a ApiClientProvider');
	return apiClientCtx;
}
